import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as tenantApi from '../../services/api/tenantApi';

// ─── Async Thunks ────────────────────────────────────────────────────────────

export const fetchTenants = createAsyncThunk(
  'tenants/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch tenants' }
      );
    }
  }
);

export const fetchTenantById = createAsyncThunk(
  'tenants/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch tenant details' }
      );
    }
  }
);

export const createTenant = createAsyncThunk(
  'tenants/create',
  async (tenantData, { rejectWithValue }) => {
    try {
      const response = await tenantApi.create(tenantData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to create tenant' }
      );
    }
  }
);

export const updateTenant = createAsyncThunk(
  'tenants/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await tenantApi.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to update tenant' }
      );
    }
  }
);

export const suspendTenant = createAsyncThunk(
  'tenants/suspend',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await tenantApi.suspend(id, reason);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to suspend tenant' }
      );
    }
  }
);

export const reactivateTenant = createAsyncThunk(
  'tenants/reactivate',
  async (id, { rejectWithValue }) => {
    try {
      const response = await tenantApi.reactivate(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to reactivate tenant' }
      );
    }
  }
);

export const offboardTenant = createAsyncThunk(
  'tenants/offboard',
  async ({ id, options }, { rejectWithValue }) => {
    try {
      const response = await tenantApi.offboard(id, options);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to initiate offboarding' }
      );
    }
  }
);

export const fetchTenantAudit = createAsyncThunk(
  'tenants/fetchAudit',
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getAuditLog(id, params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch audit log' }
      );
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState = {
  // Tenant list
  list: [],
  pagination: {
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 0,
  },
  filters: {
    search: '',
    status: null,
    plan: null,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  listLoading: false,
  listError: null,

  // Tenant detail
  current: null,
  detailLoading: false,
  detailError: null,

  // Audit log
  auditLog: [],
  auditPagination: { total: 0, page: 1, pageSize: 25, totalPages: 0 },
  auditLoading: false,

  // Action states
  actionLoading: false,
  actionError: null,
  actionSuccess: null,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const tenantSlice = createSlice({
  name: 'tenants',
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters(state) {
      state.filters = initialState.filters;
    },
    clearCurrentTenant(state) {
      state.current = null;
      state.detailError = null;
    },
    clearActionStatus(state) {
      state.actionError = null;
      state.actionSuccess = null;
    },
  },
  extraReducers: (builder) => {
    // ── Fetch Tenants List ──
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload?.message || 'Failed to fetch tenants';
      });

    // ── Fetch Tenant Detail ──
    builder
      .addCase(fetchTenantById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchTenantById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchTenantById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload?.message || 'Failed to fetch tenant';
      });

    // ── Create Tenant ──
    builder
      .addCase(createTenant.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
        state.actionSuccess = null;
      })
      .addCase(createTenant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = 'Tenant created successfully';
        state.list.unshift(action.payload);
      })
      .addCase(createTenant.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Failed to create tenant';
      });

    // ── Update Tenant ──
    builder
      .addCase(updateTenant.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateTenant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = 'Tenant updated successfully';
        state.current = action.payload;
        const idx = state.list.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.list[idx] = { ...state.list[idx], ...action.payload };
      })
      .addCase(updateTenant.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Failed to update tenant';
      });

    // ── Suspend Tenant ──
    builder
      .addCase(suspendTenant.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(suspendTenant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = 'Tenant suspended';
        if (state.current?.id === action.payload.id) {
          state.current.status = 'suspended';
        }
        const idx = state.list.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.list[idx].status = 'suspended';
      })
      .addCase(suspendTenant.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Failed to suspend tenant';
      });

    // ── Reactivate Tenant ──
    builder
      .addCase(reactivateTenant.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(reactivateTenant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = 'Tenant reactivated';
        if (state.current?.id === action.payload.id) {
          state.current.status = 'active';
        }
        const idx = state.list.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.list[idx].status = 'active';
      })
      .addCase(reactivateTenant.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Failed to reactivate tenant';
      });

    // ── Offboard Tenant ──
    builder
      .addCase(offboardTenant.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(offboardTenant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = 'Offboarding initiated';
        if (state.current?.id === action.payload.id) {
          state.current.status = 'offboarding';
        }
        const idx = state.list.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.list[idx].status = 'offboarding';
      })
      .addCase(offboardTenant.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Failed to offboard tenant';
      });

    // ── Fetch Audit Log ──
    builder
      .addCase(fetchTenantAudit.pending, (state) => {
        state.auditLoading = true;
      })
      .addCase(fetchTenantAudit.fulfilled, (state, action) => {
        state.auditLoading = false;
        state.auditLog = action.payload.data;
        state.auditPagination = action.payload.pagination;
      })
      .addCase(fetchTenantAudit.rejected, (state) => {
        state.auditLoading = false;
      });
  },
});

export const { setFilters, clearFilters, clearCurrentTenant, clearActionStatus } =
  tenantSlice.actions;

export default tenantSlice.reducer;
